import { clamp01, evalKeys, toTransform } from './tween';

/* Turns one scene animator into its style at loop time t, and writes it to
   the DOM only when it changed. Shared by the live stage, the scene lab and
   the validator (which counts how many elements move at once). */

const countAt = (t, start, per, len) => (t < start ? 0 : Math.min(len, Math.floor((t - start) / per) + 1));

export function outputOf(a, t) {
  switch (a.kind) {
    case 'tf': {
      const v = evalKeys(a.ks, t);
      const o = clamp01(v.o);
      // a hidden element does not move, whatever its keys say
      return o <= 0.001 ? { transform: 'hidden', opacity: 0 } : { transform: toTransform(v), opacity: o };
    }
    case 'draw': {
      const v = evalKeys(a.ks, t);
      return { dash: (1 - clamp01(v.d)) * 100, opacity: clamp01(v.o) };
    }
    case 'pulse': {
      const v = evalKeys(a.ks, t);
      return { dash: 14 - 114 * clamp01(v.d), opacity: clamp01(v.o) };
    }
    case 'type': {
      const chars = a.chars || (a.chars = Array.from(a.text));
      return { count: t >= a.until ? chars.length : countAt(t, a.start, a.per, chars.length) };
    }
    case 'caret': {
      const w = a.windows.find((x) => t >= x.from && t < x.to);
      if (!w) return { opacity: 0 };
      const typing = t >= w.typing[0] && t < w.typing[1];
      return { opacity: typing ? 1 : (Math.floor((t - w.from) / 530) % 2 === 0 ? 1 : 0) };
    }
    default:
      return {};
  }
}

const same = (a, b) => a && b && a.transform === b.transform && a.opacity === b.opacity && a.dash === b.dash && a.count === b.count;

export const changed = (a, t0, t1) => !same(outputOf(a, t0), outputOf(a, t1));

/* Write an animator's style to its elements. `cache` holds the last output
   per animator so an unchanged element costs nothing. */
export function applyAnimator(a, els, t, cache) {
  const out = outputOf(a, t);
  const prev = cache.get(a);
  if (same(out, prev)) return;
  cache.set(a, out);
  els.forEach((el) => {
    if (!el) return;
    if (a.kind === 'type') {
      const chars = a.chars;
      const typed = el.firstChild;
      const ghost = el.lastChild;
      if (typed && ghost && typed !== ghost) {
        typed.textContent = chars.slice(0, out.count).join('');
        ghost.textContent = chars.slice(out.count).join('');
      } else el.textContent = chars.slice(0, out.count).join('');
      if (a.scroll) {
        // A real input keeps the caret in view: shift the line left once it
        // is wider than the box (phones).
        const box = el.parentElement;
        const over = box ? Math.max(0, typed.offsetWidth - box.clientWidth + 6) : 0;
        el.style.transform = over ? `translate3d(${-over}px,0,0)` : 'none';
        if (box) box.setAttribute('data-over', String(over > 0));
      }
      return;
    }
    if (a.kind === 'caret') {
      el.style.opacity = String(out.opacity);
      return;
    }
    if (out.transform !== undefined && out.transform !== 'hidden') el.style.transform = out.transform;
    if (out.opacity !== undefined) {
      // always an explicit value: many scene nodes default to opacity 0 in CSS
      el.style.opacity = String(Math.round(out.opacity * 1000) / 1000);
      el.style.visibility = out.opacity <= 0.001 ? 'hidden' : '';
    }
    if (out.dash !== undefined) el.style.strokeDashoffset = String(Math.round(out.dash * 100) / 100);
  });
}
